'use client';

import Icon from '@/components/ui/AppIcon';
import DemoDataPill from './DemoDataPill';

const cardStyle = {
  background: '#FFFFFF',
  boxShadow: '0 2px 12px rgba(0,0,0,0.06)',
  border: '1px solid #F0F0F0',
};

export default function Row6RiskAndAction() {
  return (
    <section className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
      <CustomerConcentration />
      <OpenRiskItems />
      <CEOInbox />
      <StrategicDecisions />
    </section>
  );
}

// ── 1. Customer Concentration ──────────────────────────────────────────────

const TOP_CUSTOMERS: { name: string; revenue: number }[] = [];
const YTD_REVENUE_TOTAL = 0;
const CONCENTRATION_THRESHOLD_PCT = 25;

function CustomerConcentration() {
  const rows = TOP_CUSTOMERS.map((c) => ({
    ...c,
    pct: YTD_REVENUE_TOTAL > 0 ? (c.revenue / YTD_REVENUE_TOTAL) * 100 : 0,
  }));
  // Guard: empty customer list would give Math.max(...[]) = -Infinity.
  const maxPct = rows.length ? Math.max(...rows.map((r) => r.pct)) : 0;
  const flagged = rows.some((r) => r.pct > CONCENTRATION_THRESHOLD_PCT);

  return (
    <div className="relative rounded-xl p-5" style={cardStyle}>
      <DemoDataPill />
      <div className="flex items-start justify-between gap-2 mb-3 pr-16">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <Icon name="UsersIcon" size={15} style={{ color: '#033494' }} />
            <h3 className="text-base font-bold" style={{ color: '#000150', fontFamily: 'Inter, Poppins, sans-serif' }}>
              Customer Concentration
            </h3>
          </div>
          <p className="text-xs text-gray-400 mt-0.5">Top 5 · % of YTD revenue</p>
        </div>
      </div>
      {flagged && (
        <div
          className="rounded-lg px-2.5 py-1.5 mb-2 text-[10px] font-bold uppercase tracking-wider"
          style={{ background: 'rgba(239,68,68,0.10)', color: '#B91C1C', border: '1px solid rgba(239,68,68,0.25)' }}
        >
          Concentration risk · top customer &gt;{CONCENTRATION_THRESHOLD_PCT}%
        </div>
      )}
      <div className="space-y-1.5">
        {rows.map((r) => {
          const isFlagged = r.pct > CONCENTRATION_THRESHOLD_PCT;
          const barColor = isFlagged ? '#EF4444' : '#07B0E9';
          return (
            <div key={r.name}>
              <div className="flex items-center justify-between text-[11px] mb-0.5">
                <span className="font-semibold text-gray-700 truncate">{r.name}</span>
                <span className="font-bold flex-shrink-0 ml-2" style={{ color: isFlagged ? '#B91C1C' : '#000150' }}>
                  {r.pct.toFixed(1)}%
                </span>
              </div>
              <div className="h-1.5 rounded-full" style={{ background: '#F3F4F6' }}>
                <div
                  className="h-1.5 rounded-full"
                  style={{ width: `${(r.pct / maxPct) * 100}%`, background: barColor }}
                />
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ── 2. Open Risk Items ─────────────────────────────────────────────────────

interface RiskItem {
  category: 'lawsuit' | 'osha' | 'permit' | 'covenant' | 'insurance';
  icon: string;
  iconColor: string;
  brief: string;
  severity: 'high' | 'medium' | 'low';
}

const RISK_ITEMS: RiskItem[] = [];

function severityPill(sev: RiskItem['severity']): { color: string; bg: string; label: string } {
  if (sev === 'high')   return { color: '#B91C1C', bg: 'rgba(239,68,68,0.15)',  label: 'High' };
  if (sev === 'medium') return { color: '#92400E', bg: 'rgba(245,158,11,0.15)', label: 'Medium' };
  return                       { color: '#0369A1', bg: 'rgba(7,176,233,0.10)',  label: 'Low' };
}

function OpenRiskItems() {
  const highCount = RISK_ITEMS.filter((r) => r.severity === 'high').length;
  return (
    <div className="relative rounded-xl p-5" style={cardStyle}>
      <DemoDataPill />
      <div className="flex items-start justify-between gap-2 mb-3 pr-16">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <Icon name="ExclamationTriangleIcon" size={15} style={{ color: '#EF4444' }} />
            <h3 className="text-base font-bold" style={{ color: '#000150', fontFamily: 'Inter, Poppins, sans-serif' }}>
              Open Risk Items
            </h3>
          </div>
          <p className="text-xs text-gray-400 mt-0.5">
            {RISK_ITEMS.length} open · {highCount} high
          </p>
        </div>
      </div>
      <div className="space-y-2">
        {RISK_ITEMS.map((r, i) => {
          const sev = severityPill(r.severity);
          return (
            <div
              key={i}
              className="flex items-start gap-2 rounded-lg px-2.5 py-2"
              style={{ border: '1px solid #F3F4F6' }}
            >
              <div
                className="w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0"
                style={{ background: '#F8F9FA' }}
              >
                <Icon name={r.icon} size={14} style={{ color: r.iconColor }} />
              </div>
              <p className="flex-1 text-[11px] text-gray-700 leading-snug min-w-0">{r.brief}</p>
              <span
                className="rounded-full px-2 py-0.5 text-[10px] font-bold flex-shrink-0"
                style={{ background: sev.bg, color: sev.color }}
              >
                {sev.label}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ── 3. CEO Inbox ───────────────────────────────────────────────────────────

interface InboxItem {
  source: string;
  brief: string;
  ageLabel: string;
  awaiting: 'Mike' | 'Pat';
}

const CEO_INBOX: InboxItem[] = [];

function CEOInbox() {
  const total = CEO_INBOX.length;
  const awaitingMike = CEO_INBOX.filter((i) => i.awaiting === 'Mike').length;
  const awaitingPat = CEO_INBOX.filter((i) => i.awaiting === 'Pat').length;
  const top = CEO_INBOX.slice(0, 3);

  return (
    <div className="relative rounded-xl p-5" style={cardStyle}>
      <DemoDataPill />
      <div className="flex items-start justify-between gap-2 mb-3 pr-16">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <Icon name="InboxIcon" size={15} style={{ color: '#07B0E9' }} />
            <h3 className="text-base font-bold" style={{ color: '#000150', fontFamily: 'Inter, Poppins, sans-serif' }}>
              CEO Inbox
            </h3>
          </div>
          <p className="text-xs text-gray-400 mt-0.5">
            {total} open · {awaitingMike} awaiting Mike · {awaitingPat} awaiting Pat
          </p>
        </div>
      </div>
      <div className="space-y-2">
        {top.map((item, i) => (
          <div
            key={i}
            className="rounded-lg px-2.5 py-2"
            style={{ border: '1px solid #F3F4F6' }}
          >
            <div className="flex items-center justify-between gap-2">
              <span className="text-[10px] uppercase tracking-wider font-semibold text-gray-500 truncate">
                {item.source}
              </span>
              <span
                className="rounded-full px-2 py-0.5 text-[9px] font-bold flex-shrink-0"
                style={{ background: 'rgba(3,52,148,0.08)', color: '#033494' }}
              >
                @{item.awaiting}
              </span>
            </div>
            <p className="text-[11px] text-gray-700 mt-0.5 leading-snug">{item.brief}</p>
            <p className="text-[10px] text-gray-400 mt-0.5">{item.ageLabel} ago</p>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── 4. Strategic Decisions Pending ─────────────────────────────────────────

interface DecisionItem {
  decision: string;
  assignee: 'Mike' | 'Pat';
  ageLabel: string;
}

const DECISIONS: DecisionItem[] = [];

function StrategicDecisions() {
  const total = DECISIONS.length;
  const awaitingMike = DECISIONS.filter((d) => d.assignee === 'Mike').length;
  const awaitingPat = DECISIONS.filter((d) => d.assignee === 'Pat').length;
  const top = DECISIONS.slice(0, 3);

  return (
    <div className="relative rounded-xl p-5" style={cardStyle}>
      <DemoDataPill />
      <div className="flex items-start justify-between gap-2 mb-3 pr-16">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <Icon name="CheckBadgeIcon" size={15} style={{ color: '#8B5CF6' }} />
            <h3 className="text-base font-bold" style={{ color: '#000150', fontFamily: 'Inter, Poppins, sans-serif' }}>
              Strategic Decisions
            </h3>
          </div>
          <p className="text-xs text-gray-400 mt-0.5">
            {total} pending · {awaitingMike} awaiting Mike · {awaitingPat} awaiting Pat
          </p>
        </div>
      </div>
      <div className="space-y-2">
        {top.map((d, i) => (
          <div
            key={i}
            className="rounded-lg px-2.5 py-2"
            style={{ border: '1px solid #F3F4F6' }}
          >
            <div className="flex items-center justify-between gap-2 mb-0.5">
              <p className="text-[11px] font-semibold text-gray-700 leading-snug flex-1 min-w-0">{d.decision}</p>
              <span
                className="rounded-full px-2 py-0.5 text-[9px] font-bold flex-shrink-0"
                style={{ background: 'rgba(139,92,246,0.10)', color: '#7C3AED' }}
              >
                @{d.assignee}
              </span>
            </div>
            <p className="text-[10px] text-gray-400">{d.ageLabel} pending</p>
          </div>
        ))}
      </div>
    </div>
  );
}
